// Deterministic xoshiro128++ pseudo-random generator.
//
// 128-bit state across four 32-bit words, period 2^128 - 1, all 32-bit ops
// (no BigInt). The C reference implementation lives at
// https://prng.di.unimi.it/xoshiro128plusplus.c and is in the public domain.
//
// Why this PRNG: the simulator must be byte-for-byte deterministic across
// browsers and Node so PvP replays and golden tests stay stable. xoshiro128++
// is fast in JS (32-bit math), passes BigCrush, and ships a `jump()` primitive
// we can use for splittable sub-streams.
const JUMP_CONSTANTS = [0x8764000b, 0xf542d2d3, 0x6fa035c3, 0x77f2db5b];
function rotl32(x, k) {
    return ((x << k) | (x >>> (32 - k))) >>> 0;
}
/** Expand a single 32-bit seed into four state words via splitmix32. */
function expandSeed(seed) {
    let s = seed | 0;
    const next = () => {
        s = (s + 0x9e3779b9) | 0;
        let z = s;
        z = Math.imul(z ^ (z >>> 16), 0x85ebca6b);
        z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35);
        return (z ^ (z >>> 16)) >>> 0;
    };
    return [next(), next(), next(), next()];
}
/**
 * Mutable xoshiro128++ generator.
 *
 * Use {@link Rng.fromSeed} for fresh streams and {@link Rng.split} to derive
 * independent sub-streams (e.g. one per battle, one per AoE roll).
 */
export class Rng {
    s0;
    s1;
    s2;
    s3;
    constructor(s0, s1, s2, s3) {
        this.s0 = s0 >>> 0;
        this.s1 = s1 >>> 0;
        this.s2 = s2 >>> 0;
        this.s3 = s3 >>> 0;
    }
    /** Initialise from a 32-bit seed. The all-zero state is forbidden and is rerouted. */
    static fromSeed(seed) {
        const [a, b, c, d] = expandSeed(seed);
        if ((a | b | c | d) === 0) {
            return new Rng(1, 0, 0, 0);
        }
        return new Rng(a, b, c, d);
    }
    /** Restore a generator from a previously captured state tuple. */
    static fromState(state) {
        const [s0, s1, s2, s3] = state;
        if ((s0 | s1 | s2 | s3) === 0) {
            throw new Error('Rng state must not be all zero');
        }
        return new Rng(s0, s1, s2, s3);
    }
    /** Snapshot the internal state so it can be persisted / resumed. */
    state() {
        return [this.s0, this.s1, this.s2, this.s3];
    }
    /** Independent copy that shares no mutable state with the parent. */
    clone() {
        return new Rng(this.s0, this.s1, this.s2, this.s3);
    }
    /** Next 32-bit unsigned integer. */
    nextU32() {
        const result = (rotl32(((this.s0 + this.s3) | 0) >>> 0, 7) + this.s0) >>> 0;
        const t = (this.s1 << 9) >>> 0;
        this.s2 = (this.s2 ^ this.s0) >>> 0;
        this.s3 = (this.s3 ^ this.s1) >>> 0;
        this.s1 = (this.s1 ^ this.s2) >>> 0;
        this.s0 = (this.s0 ^ this.s3) >>> 0;
        this.s2 = (this.s2 ^ t) >>> 0;
        this.s3 = rotl32(this.s3, 11);
        return result;
    }
    /** Float in [0, 1). 24-bit precision. */
    nextFloat() {
        return this.nextU32() / 0x1_0000_0000;
    }
    /** Integer in [0, maxExclusive). */
    nextInt(maxExclusive) {
        if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
            throw new Error(`maxExclusive must be a positive integer, got ${maxExclusive}`);
        }
        return Math.floor(this.nextFloat() * maxExclusive);
    }
    /** True with probability p (default 0.5). */
    nextBool(p = 0.5) {
        return this.nextFloat() < p;
    }
    /** Float in [min, max). */
    range(min, max) {
        return min + this.nextFloat() * (max - min);
    }
    /** Uniformly pick one element. Throws on empty arrays. */
    pick(arr) {
        if (arr.length === 0) {
            throw new Error('cannot pick from empty array');
        }
        return arr[this.nextInt(arr.length)];
    }
    /**
     * Advance the stream by what would take 2^64 next() calls.
     * Use this to carve out an unreachable region and fork a sub-stream.
     */
    jump() {
        let s0 = 0;
        let s1 = 0;
        let s2 = 0;
        let s3 = 0;
        for (const word of JUMP_CONSTANTS) {
            for (let b = 0; b < 32; b++) {
                if ((word & (1 << b)) !== 0) {
                    s0 = (s0 ^ this.s0) >>> 0;
                    s1 = (s1 ^ this.s1) >>> 0;
                    s2 = (s2 ^ this.s2) >>> 0;
                    s3 = (s3 ^ this.s3) >>> 0;
                }
                this.nextU32();
            }
        }
        this.s0 = s0;
        this.s1 = s1;
        this.s2 = s2;
        this.s3 = s3;
    }
    /**
     * Fork an independent child stream and advance the parent past it.
     *
     * The child reproduces what the parent would have generated, while the
     * parent is jumped 2^64 outputs forward so the two streams will not collide
     * for any practical run length.
     */
    split() {
        const child = this.clone();
        this.jump();
        return child;
    }
}
//# sourceMappingURL=rng.js.map